/**
 * Clarix Backend Backbone Bootstrap
 * Initializes all core services in proper order
 * Must be called before rendering React components
 */

import { initializeServices as initModules } from '@/modules';
import { pbClient } from '@/services/pbClient';
import { offlineQueue } from '@/state/offlineQueue';
import { performanceMonitor } from '@/services/performanceMonitor';
import { telemetry } from '@/services/telemetry';

/**
 * Initialize all backbone services
 * Order matters: Runtime → PB Client → Offline Queue → Domain Services
 */
export async function initializeBackbone(): Promise<void> {
  console.info('[Bootstrap] Starting Clarix backbone initialization...');

  try {
    // 1. Runtime configuration is auto-loaded via RuntimeEnvironment constructor
    console.info('[Bootstrap] Runtime configuration loaded');

    // 2. Initialize PocketBase client
    console.info('[Bootstrap] Initializing PocketBase client...');
    await pbClient.init();
    console.info('[Bootstrap] PocketBase client ready');

    // 3. Initialize offline queue
    console.info('[Bootstrap] Initializing offline queue...');
    await offlineQueue.initialize();
    console.info('[Bootstrap] Offline queue ready');

    // 4. Initialize telemetry channels
    console.info('[Bootstrap] Initializing telemetry channels...');
    telemetry.initialize();
    performanceMonitor.initialize();
    console.info('[Bootstrap] Telemetry ready');

    // Try to flush any pending operations after initialization
    if (navigator.onLine && offlineQueue.getState().items.length > 0) {
      console.info('[Bootstrap] Flushing pending offline operations...');
      await offlineQueue.flush();
    }

    console.info('[Bootstrap] ✓ Backbone initialized successfully');
  } catch (err) {
    telemetry.trackError(err, 'bootstrap.initialize');
    console.error('[Bootstrap] ✗ Initialization failed:', err);
    throw err;
  }
}

/**
 * Initialize domain services with authenticated user context
 * Call this after successful authentication
 */
export async function initializeServices(userId: string): Promise<void> {
  console.info('[Bootstrap] Initializing domain services for user:', userId);

  try {
    initModules(userId);
    console.info('[Bootstrap] ✓ Domain services initialized');
  } catch (err) {
    telemetry.trackError(err, 'bootstrap.initializeServices', { userId });
    console.error('[Bootstrap] ✗ Service initialization failed:', err);
    throw err;
  }
}

/**
 * Graceful shutdown (optional)
 */
export async function shutdownBackbone(): Promise<void> {
  console.info('[Bootstrap] Shutting down Clarix backbone...');

  // Flush any pending operations before shutdown
  if (offlineQueue.getState().items.length > 0) {
    console.info('[Bootstrap] Flushing pending operations on shutdown...');
    try {
      await offlineQueue.flush();
    } catch (err) {
      console.warn('[Bootstrap] Failed to flush queue on shutdown:', err);
    }
  }

  await telemetry.flush();
  await performanceMonitor.flush();
  telemetry.shutdown();
  performanceMonitor.shutdown();

  console.info('[Bootstrap] Backbone shutdown complete');

}
